import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface CalendarModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSelectDate: (date: Date) => void;
    selectedDate?: Date;
    container?: HTMLElement | null;
}

const MONTHS = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const DAYS = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa', 'Do'];

const CalendarModal: React.FC<CalendarModalProps> = ({
    open,
    onOpenChange,
    onSelectDate,
    selectedDate,
    container
}) => {
    const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
    const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

    useEffect(() => {
        if (open) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [open]);

    const getDaysInMonth = (month: number, year: number) => {
        return new Date(year, month + 1, 0).getDate();
    };

    const getFirstDayOfMonth = (month: number, year: number) => {
        const day = new Date(year, month, 1).getDay();
        // Convertir domingo (0) a 7 para que lunes sea 1
        return day === 0 ? 7 : day;
    };

    const handlePreviousMonth = () => {
        if (currentMonth === 0) {
            setCurrentMonth(11);
            setCurrentYear(currentYear - 1);
        } else {
            setCurrentMonth(currentMonth - 1);
        }
    };

    const handleNextMonth = () => {
        if (currentMonth === 11) {
            setCurrentMonth(0);
            setCurrentYear(currentYear + 1);
        } else {
            setCurrentMonth(currentMonth + 1);
        }
    };

    const handleDateClick = (day: number) => {
        const date = new Date(currentYear, currentMonth, day);
        onSelectDate(date);
        onOpenChange(false);
    };

    const handleClose = () => {
        onOpenChange(false);
    };

    const renderCalendarDays = () => {
        const daysInMonth = getDaysInMonth(currentMonth, currentYear);
        const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
        const daysInPrevMonth = getDaysInMonth(currentMonth - 1, currentYear);

        const days = [];

        // Previous month days
        for (let i = firstDay - 1; i > 0; i--) {
            days.push({
                day: daysInPrevMonth - i + 1,
                isCurrentMonth: false,
                isSelected: false
            });
        }

        // Current month days
        for (let day = 1; day <= daysInMonth; day++) {
            const isSelected = selectedDate &&
                selectedDate.getDate() === day &&
                selectedDate.getMonth() === currentMonth &&
                selectedDate.getFullYear() === currentYear;

            days.push({
                day,
                isCurrentMonth: true,
                isSelected: !!isSelected
            });
        }

        // Next month days to fill the grid
        const remainingDays = 42 - days.length; // 6 weeks * 7 days
        for (let day = 1; day <= remainingDays; day++) {
            days.push({
                day,
                isCurrentMonth: false,
                isSelected: false
            });
        }

        // Group into weeks
        const weeks = [];
        for (let i = 0; i < days.length; i += 7) {
            weeks.push(days.slice(i, i + 7));
        }

        return weeks;
    };

    if (!open) return null;

    const weeks = renderCalendarDays();

    return createPortal(
        <div className="fixed inset-0 z-[200] flex items-center justify-center">
            {/* Overlay */}
            <div
                className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                onClick={handleClose}
            />

            {/* Calendar */}
            <div className="relative w-full max-w-[330px] px-6 py-7 border border-border rounded-2xl bg-card shadow-2xl">
                {/* Month Navigation */}
                <div className="flex items-center gap-2 mb-4">
                    <div className="flex items-center gap-8 border border-border w-full justify-between rounded-xl py-0.5 px-0.5 text-sm font-medium text-foreground">
                        <button
                            onClick={handlePreviousMonth}
                            className="p-2 transition-all duration-500 rounded-lg text-foreground hover:bg-primary/10 hover:text-primary"
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </button>
                        <span className="font-semibold">
                            {MONTHS[currentMonth]} {currentYear}
                        </span>
                        <button
                            onClick={handleNextMonth}
                            className="p-2 transition-all duration-500 rounded-lg text-foreground hover:bg-primary/10 hover:text-primary"
                        >
                            <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {/* Calendar Table */}
                <table className="w-full">
                    {/* Days of Week Header */}
                    <thead className="mb-2">
                        <tr className="flex">
                            {DAYS.map((day) => (
                                <td key={day} className="flex items-center justify-center w-10 h-10">
                                    <p className="text-sm font-medium text-muted-foreground">
                                        {day}
                                    </p>
                                </td>
                            ))}
                        </tr>
                    </thead>

                    {/* Calendar Days */}
                    <tbody>
                        {weeks.map((week, weekIndex) => (
                            <tr key={weekIndex} className="flex">
                                {week.map((dayObj, dayIndex) => (
                                    <td key={dayIndex} className="flex items-center justify-center w-10 h-10">
                                        <button
                                            onClick={() => dayObj.isCurrentMonth && handleDateClick(dayObj.day)}
                                            disabled={!dayObj.isCurrentMonth}
                                            className={`text-sm font-medium rounded-full flex items-center justify-center w-full h-full transition-all duration-300 ${dayObj.isSelected
                                                    ? 'bg-primary text-primary-foreground'
                                                    : dayObj.isCurrentMonth
                                                        ? 'text-foreground hover:bg-primary/10 hover:text-primary'
                                                        : 'text-muted-foreground cursor-default'
                                                }`}
                                        >
                                            {dayObj.day}
                                        </button>
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>,
        container || document.body
    );
};

export default CalendarModal;
